import { useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box
} from '@mui/material';
import { downloadFile, getDownloadFileName } from '../../../helpers';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { getNeighborhoodPriceGuide } from '../../../fetch';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { SalesNeighborhoodDropdown } from '../../../components/dropdowns/sales-neighborhood-dropdown';

export const NeighborhoodPriceGuide = ({ shouldSeeAllNeighborhoods }: { shouldSeeAllNeighborhoods: boolean }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<number | string>('');
  const [use3CarFootages, setUse3CarFootages] = useState<boolean>(false);

  const fetchNeighborhoodPriceGuide = async () => {
    try {
      setIsLoading(true);
      const res = await getNeighborhoodPriceGuide(selectedNeighborhoodId, use3CarFootages);
      downloadFile(res.data, getDownloadFileName(res) ?? 'neighborhood-price-sheet.pdf');
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error loading price guide, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Accordion className={classes.root}>
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.topPanelSummaryWrapper}>
            <Typography variant="h6">Neighborhood Price Sheet</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <div className={classes.panelSummaryWrapper}>
            <Box mt={1}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <SalesNeighborhoodDropdown
                    isRequired={true}
                    isDisabled={isLoading}
                    value={selectedNeighborhoodId}
                    handleChange={val => {
                      setSelectedNeighborhoodId(Number(val));
                    }}
                    apiParams={{ isActive: true, allNeighborhoods: shouldSeeAllNeighborhoods }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl component="fieldset" size="small" fullWidth>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={use3CarFootages}
                            name="3-car-footages"
                            onChange={e => {
                              e.target.checked
                                ? setUse3CarFootages(true)
                                : setUse3CarFootages(false);
                            }}
                            disabled={!selectedNeighborhoodId}
                          />
                        }
                        label="Use 3 Car Footages"
                      />
                    </FormGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <LoadingButton
                    id="generate-price-guide"
                    color="primary"
                    variant="contained"
                    onClick={fetchNeighborhoodPriceGuide}
                    disabled={isLoading || !selectedNeighborhoodId}
                    loading={isLoading}
                  >
                    Generate & Download Price Guide
                  </LoadingButton>
                </Grid>
              </Grid>
            </Box>
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  }
}));
